import Vue from 'vue'
import Base from './Base'
import Model from './Model'
import { MissingModel, InvalidModelProvided, WrongModel } from '../errors/Collection.errors'

export default class Collection extends Base {
  constructor (models = []) {
    super()

    this.validateModel()

    Vue.set(this, '_models', [])
    this.add(models)
  }

  /**
   * Collection model
   *
   * @return {Model}
   */
  model () {}

  /**
   * Add model to collection
   *
   * @param {Model<this.model()>} model
   *
   * @returns {Model<this.model()>|Model<this.model()>[]}
   */
  add (model) {
    if (model instanceof Array) {
      return model.map(_model => this.add(_model))
    } else if (model instanceof this.model()) {
      this._models.push(model)
      return model
    } else if (model instanceof Object) {
      const newInstance = new (this.model())(model)
      this._models.push(newInstance)
      return newInstance
    }

    throw new InvalidModelProvided({ collection: this.name(), model })
  }

  /**
   * Find model by id
   *
   * @param {String|Number} id
   *
   * @returns {Model<this.model()>}
   */
  find (id) {
    return this._models.find(_model => _model.get(_model.$primaryKey()) === id)
  }

  /**
   * Filter collection
   *
   * @param {Function} callback
   *
   * @returns {Collection<this.name()>}
   */
  filter (callback) {
    return new this.constructor(this._models.filter(callback))
  }

  /**
   * Convert collection instances into object
   *
   * @returns {Object}
   */
  toArray () {
    return this._models.map(_model => _model.attributes())
  }

  /**
   * Get collection count
   *
   * @returns {Number}
   */
  count () {
    return this._models.length
  }

  /**
   * Validate model
   *
   * @returns {Throwable|void}
   */
  validateModel () {
    if (this.model() === undefined) {
      throw new MissingModel({ collection: this.name() })
    }

    if (!(this.model().prototype instanceof Model || this.model() === Model)) {
      throw new WrongModel({ collection: this.name(), model: this.model().name })
    }
  }

  /**
   * Generate csv from the collection
   *
   * @param {Array} columns
   */
  generateCsv (columns) {
    return new Promise((resolve) => {
      Promise.all(this._models.map(_model => _model.generateCsvRow(columns)))
        .then((rows) => {
          const headers = Object.keys(rows[0])

          resolve({
            headers,
            rows
          })
        })
    })
  }

  /*
   * Loop through the collection
   *
   * @param {Function} callback
   */
  loop (callback) {
    this._models.forEach(callback)
  }
}
